import {useReactiveVar} from '@apollo/client';

import {surveyVar, surveyWizardVars} from '#apollo/local/SurveyVar';
import {QuestionChoiceTypeEnum, QuestionTypeEnum, SurveyQuestionFragment} from '#apollo/schema';

/**
 * Given a survey ID and a Question record, `useSurveyVar` is a hook that returns
 * helpers for reading the current value of, and updating the answer for that question.
 *
 * It returns an `onChange` handler that will perform the necessary validations
 * on the received `event.target` value, and update the reactive var with the
 * given answer.
 *
 * It also returns a `currentValue`, which indicates the current answers' own
 * `answer` field value.
 */
export function useSurveyVar({
  surveyId = '',
  question,
}: {
  surveyId: string;
  question: SurveyQuestionFragment;
}) {
  const savedSurveys = useReactiveVar(surveyVar) || {};
  const {id, multipleChoiceConfiguration, questionType} = question;
  const isMultipleChoice =
    multipleChoiceConfiguration?.questionChoiceType === QuestionChoiceTypeEnum.MultiEnum;
  const surveyAnswers = savedSurveys?.[surveyId] || [];
  const questionAnswer = surveyAnswers[id] || {
    questionId: id,
    questionType,
    answer: questionType === QuestionTypeEnum.MultipleChoiceEnum ? [] : '',
  };

  const handleChange = ({target}: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    switch (questionAnswer.questionType) {
      case QuestionTypeEnum.MultipleChoiceEnum: {
        // If targetValue already exists in the questionAnswer array, remove it.
        if (questionAnswer.answer?.includes(target.value)) {
          surveyWizardVars.update({
            surveyId,
            answer: {
              ...questionAnswer,
              answer: questionAnswer.answer.filter(entry => entry !== target.value),
            },
          });
        } else if (
          isMultipleChoice &&
          !(target.value == 'option-112' || target.value == 'option-76')
        ) {
          // If multiple answers may be selected, add target.value to questionAnswer array.
          // option-112 represents "Prefer not to answer"
          // option-76 represents "None of the above"
          // If the user selects either after other options were selected, all other options will be
          // deselected. If the user selects other options after selecting either, option-112/ option-76
          // will be deselected.
          const excludedVals = ['option-112', 'option-76'];
          const filteredAnswers = questionAnswer.answer.filter(
            entry => !excludedVals.includes(entry),
          );
          surveyWizardVars.update({
            surveyId,
            answer: {
              ...questionAnswer,
              answer: [...filteredAnswers, target.value],
            },
          });
        } else {
          // Otherwise, set questionAnswer array to include _only_ the target.value.
          surveyWizardVars.update({
            surveyId,
            answer: {...questionAnswer, answer: [target.value]},
          });
        }

        break;
      }
      case QuestionTypeEnum.NumericEnum: {
        if (target.value === '' || /[0-9]+/.test(target.value)) {
          surveyWizardVars.update({
            surveyId,
            answer: {...questionAnswer, answer: parseInt(target.value) || ''},
          });
        }

        break;
      }
      default:
        surveyWizardVars.update({surveyId, answer: {...questionAnswer, answer: target.value}});
    }
  };

  return {onChange: handleChange, currentValue: questionAnswer.answer};
}
